import {
  BookOpenCheck, Building2, CircleHelp, FolderOpen, Landmark, LifeBuoy, ListTodo, Shield, ShieldCheck,
} from "lucide-react";
import type { FrameworkId } from "@main/context/FrameworksContext";
import { CLIENT_MIN_TIER, deferFor, tierLock } from "@main/nav/clientNav";
import type { ClientNavCounts } from "@main/nav/NavCountsContext";
import type { NavItem, NavSection } from "@main/nav/types";

// ── Jersey edition navigation ─────────────────────────────────────────────
// The Jersey edition (backend/src/lib/editions.ts) is Cyber Essentials / CE+,
// the Cyber Security (Jersey) Law 2026 and Computer Misuse law, supplier risk,
// the OES engine and incident reporting to the JCSC. No data protection module,
// no other standard, no CIS hardening - the API refuses those for a Jersey
// organisation, so nothing here links to them.
//
// Built the same way as the main client shell (frontend/src/nav/clientNav.ts):
// a few task areas, each one sidebar row with its pages as tabs above the page;
// pages that do not apply yet, or that Guided mode keeps out of the way, and
// pages above the package's tier are listed on More tools rather than in the
// sidebar (nav/areas.ts). Reporting an incident to the JCSC is never filed
// away: a 24-hour clock can start any day.

export interface JsyNavInput {
  tierLevel: number | null;
  enrolled: ReadonlySet<FrameworkId>;
  permissions: string[];
  counts: ClientNavCounts | null;
  tprm: boolean;
  /** Guided experience mode (the default): the short menu. */
  guided?: boolean;
}

export function buildJsyNav(input: JsyNavInput): NavSection[] {
  const { tierLevel, enrolled, permissions, counts } = input;
  const guided = input.guided ?? false;
  const has = (p: string) => permissions.includes(p);
  const isAdmin = has("org:admin");
  // Narrower permissions carved out of org:admin / compliance:run (see frontend/src/nav/clientNav.ts).
  const canReport = has("reports:generate");
  const canIntegrate = has("integrations:admin");
  const canBilling = has("billing:view");
  const canPeople = has("people:write");
  const plus = enrolled.has("cyber_essentials_plus");
  const usage = counts?.usage;
  const used = (n: number | undefined) => (n ?? 0) > 0;
  const defer = (opts: { relevant?: boolean; advanced?: boolean; used?: boolean }) => deferFor(guided, opts);

  const home: NavItem = {
    id: "home", label: "Home", icon: ListTodo, to: "/actions", badge: "actions",
    tabs: [
      { id: "actions", label: "What's next", to: "/actions", badge: "actions", description: "Everything that needs doing, most urgent first", keywords: ["home", "tasks", "to do", "my actions", "setup"] },
      { id: "dashboard", label: "Overview", to: "/dashboard", description: "Your certification, the Jersey law and your scores", keywords: ["dashboard", "scores"] },
      { id: "guides", label: "Guides", to: "/guides", description: "Step-by-step: adopt your first policy, prepare for your assessment", keywords: ["guided", "walkthrough", "how to", "steps"] },
      {
        id: "guidance", label: "AI advice", to: "/guidance",
        lock: tierLock(tierLevel, CLIENT_MIN_TIER.nextSteps, "AI advice"),
        deferred: defer({ advanced: true }),
        description: "Recommendations from the latest compliance check",
        keywords: ["next steps", "guidance", "recommendations"],
      },
      { id: "schedule", label: "Calendar", to: "/schedule", description: "Renewals and reviews coming up", keywords: ["review calendar", "renewal", "reviews"] },
      {
        id: "my-tasks", label: "My tasks", to: "/my-tasks", badge: "myTasks",
        deferred: defer({ relevant: used(usage?.policyCampaigns) || used(usage?.trainingAssignments), used: (counts?.myTasks ?? 0) > 0 }),
        description: "Policies to read, training to complete and approvals waiting for you",
        keywords: ["policy acknowledgement", "read and sign", "training", "approve", "tasks"],
      },
    ],
  };

  const certification: NavItem = {
    id: "certification-area", label: plus ? "Cyber Essentials Plus" : "Cyber Essentials", icon: ShieldCheck, badge: "runningCheck",
    tabs: [
      plus
        ? { id: "cyber-essentials-plus", label: "Assessment", to: "/cyber-essentials-plus", description: "The Cyber Essentials Plus requirements and your answers", keywords: ["ce+", "cyber essentials plus", "iasme", "audit"] }
        : { id: "cyber-essentials", label: "Assessment", to: "/cyber-essentials", description: "The five Cyber Essentials controls and your answers", keywords: ["ce", "cyber essentials", "iasme", "self-assessment"] },
      {
        id: "compliance", label: "Compliance check", to: "/compliance", badge: "runningCheck",
        lock: tierLock(tierLevel, CLIENT_MIN_TIER.complianceCheck, "AI Compliance Check"),
        description: "Have the AI assess your evidence against Cyber Essentials",
        keywords: ["ai", "check", "run", "assessment"],
      },
      {
        // Cyber Essentials requires critical and high updates within 14 days; this is where that is shown.
        id: "vulnerabilities", label: "Security updates", to: "/vulnerabilities", badge: "vulnerabilities",
        description: "Every vulnerability on your devices with its 14-day Cyber Essentials deadline",
        keywords: ["vulnerabilities", "patching", "security update management", "14 days", "nessus", "defender", "cve"],
      },
      {
        id: "auditor-feedback", label: "Assessor feedback", to: "/auditor-feedback", badge: "unresolvedFeedback",
        lock: tierLock(tierLevel, CLIENT_MIN_TIER.auditorFeedback, "Assessor Feedback"),
        description: "Findings and evidence requests from your certification body",
        keywords: ["findings", "certification body", "assessor"],
      },
    ],
  };

  const law: NavItem = {
    id: "law-area", label: "Jersey cyber law", icon: Landmark, badge: "submissions",
    tabs: [
      { id: "legal", label: "Your obligations", to: "/legal", description: "What the Cyber Security (Jersey) Law 2026 asks of you", keywords: ["cyber security law 2026", "cyber security (jersey) law", "computer misuse", "cybercrime", "obligations", "oes", "essential services", "designation"] },
      {
        id: "submissions", label: "JCSC returns", to: "/submissions", badge: "submissions",
        deferred: defer({ relevant: counts?.oesPossible ?? true, used: used(usage?.submissions) || (counts?.submissions ?? 0) > 0 }),
        description: "Registrations, notifications and returns to the JCSC",
        keywords: ["jcsc submissions", "jcsc", "director", "notification", "registration", "return", "oes"],
      },
      { id: "jcsc", label: "Contact the JCSC", to: "/jcsc", description: "How to reach the Jersey Cyber Security Centre", keywords: ["jersey cyber security centre", "jcsc", "cert", "contact", "report"] },
    ],
  };

  const documents: NavItem = {
    id: "documents-area", label: "Documents", icon: FolderOpen,
    tabs: [
      { id: "templates", label: "Write policies", to: "/templates", description: "Generate a policy tailored to your organisation", keywords: ["policy templates", "generate", "docx", "templates"] },
      {
        id: "policies", label: "Your policies", to: "/policies",
        lock: tierLock(tierLevel, CLIENT_MIN_TIER.policyRepository, "Policy Repository"),
        description: "Every policy you have generated or adopted",
        keywords: ["policy repository", "generated", "documents", "policies"],
      },
      {
        id: "documents", label: "Evidence", to: "/documents",
        lock: tierLock(tierLevel, CLIENT_MIN_TIER.evidenceRepository, "Evidence Repository"),
        description: "Files that show you do what your policies say",
        keywords: ["evidence repository", "files", "uploads", "evidence"],
      },
      {
        id: "export", label: "Assessment pack", to: "/export",
        lock: tierLock(tierLevel, CLIENT_MIN_TIER.exportEvidence, "Export Evidence"),
        description: "Download everything your assessor will ask for",
        keywords: ["export evidence", "package", "zip", "assessor pack"],
      },
      {
        id: "policy-governance", label: "Policy sign-off", to: "/policy-governance", badge: "governance",
        deferred: defer({ advanced: true, used: used(usage?.policyCampaigns) || (counts?.governance ?? 0) > 0 }),
        description: "Management approval of each policy, and staff confirming they have read it",
        keywords: ["policy approval", "acknowledgement", "attestation", "distribution", "review"],
      },
      {
        id: "policy-exceptions", label: "Policy exceptions", to: "/policy-exceptions",
        deferred: defer({ advanced: true, used: used(usage?.policyExceptions) }),
        description: "Time-limited departures from a policy, approved by someone else and accepted as a risk",
        keywords: ["policy exception", "exception register", "waiver", "deviation", "risk acceptance"],
      },
      {
        id: "customer-questionnaires", label: "Customer questionnaires", to: "/customer-questionnaires",
        lock: tierLock(tierLevel, CLIENT_MIN_TIER.customerQuestionnaires, "Customer questionnaires"),
        deferred: defer({ advanced: true, used: used(usage?.customerQuestionnaires) }),
        description: "Answer the security questionnaires your customers send you, from your own records",
        keywords: ["security questionnaire", "customer questionnaire", "sig", "caiq", "vendor assessment", "due diligence"],
      },
      {
        id: "assurance-packs", label: "Assurance packs", to: "/assurance-sharing", hidden: !isAdmin,
        deferred: defer({ advanced: true, used: used(usage?.assurancePacks) }),
        description: "Share your security position with your insurer, broker or customers",
        keywords: ["insurer", "broker", "underwriter", "trust", "external review", "share"],
      },
      {
        id: "executive-report", label: "Board report", to: "/executive-report", hidden: !canReport,
        lock: tierLock(tierLevel, CLIENT_MIN_TIER.executiveReport, "Executive Report"),
        deferred: defer({ advanced: true }),
        description: "A board-ready summary of where you stand",
        keywords: ["executive report", "board", "pdf", "summary"],
      },
    ],
  };

  const risks: NavItem = {
    id: "risks-area", label: "Risks", icon: Shield,
    tabs: [
      { id: "risk-register", label: "Risk register", to: "/risk-register", description: "Your risks and how you are treating them", keywords: ["risks", "treatment"] },
      { id: "tprm", label: "Suppliers", to: "/tprm", hidden: !input.tprm, description: "Supplier risk, questionnaires and contracts", keywords: ["supplier risk", "suppliers", "third party", "vendors", "tprm"] },
      { id: "threat-intelligence", label: "Threats", to: "/threat-intelligence", deferred: defer({ advanced: true }), description: "Security advisories that affect your devices", keywords: ["threat intelligence", "advisories", "cve", "jcsc alerts"] },
      {
        id: "data-discovery", label: "Sensitive data", to: "/data-discovery", badge: "dataDiscovery",
        deferred: defer({ advanced: true, used: used(usage?.dataDiscovery) || (counts?.dataDiscovery ?? 0) > 0 }),
        description: "Where personal and sensitive data sits, from Purview, your cloud and your connected folders",
        keywords: ["data discovery", "dlp", "purview", "macie", "sensitive data", "personal data", "classification", "a.5.12"],
      },
      {
        id: "exposure", label: "Exposure", to: "/exposure",
        deferred: defer({ advanced: true, used: used(usage?.quantified) }),
        description: "What your risks could cost in a year, and against your insurance",
        keywords: ["quantified risk", "annual loss", "scenario", "insurance"],
      },
      // The platform raises corrective actions itself (a failed CE+ test, an assessor's finding) and
      // What's next links them here, so the edition carries the register they are worked in.
      {
        id: "improvement", label: "Corrective actions", to: "/improvement", badge: "improvement",
        deferred: defer({ relevant: used(usage?.correctiveActions), used: used(usage?.correctiveActions) || (counts?.improvement ?? 0) > 0 }),
        description: "Fixes for failed tests, assessor findings and incidents, with owners and due dates",
        keywords: ["corrective action", "improvement", "remediation plan", "programme", "finding"],
      },
      // Shadow AI found on your devices is raised on What's next and recorded here.
      {
        id: "ai-systems", label: "AI systems", to: "/ai-systems", badge: "aiSystems",
        deferred: defer({ advanced: true, used: used(usage?.aiSystems) || (counts?.aiSystems ?? 0) > 0 }),
        description: "The AI tools in use, including ones found on your devices",
        keywords: ["ai register", "shadow ai", "ai tools", "note-taker"],
      },
    ],
  };

  const resilienceAttention = (counts?.resilience ?? 0) > 0;
  const incidents: NavItem = {
    id: "resilience-area", label: "Incidents", icon: LifeBuoy, badge: "resilience",
    tabs: [
      { id: "resilience-incidents", label: "Report an incident", to: "/resilience/incidents", description: "Log an incident and notify the JCSC within the deadline", keywords: ["incident", "jcsc", "24 hours", "notify", "report"] },
      // The Jersey package pins Full Service, so the room is never tier-locked here.
      { id: "resilience-rooms", label: "Response rooms", to: "/resilience/rooms", description: "Run an incident, a continuity invocation or an exercise live, and keep the offline crisis pack", keywords: ["response room", "incident response", "business continuity", "situation report", "crisis pack"] },
      {
        id: "resilience", label: "Overview", to: "/resilience", exact: true, badge: "resilience",
        deferred: defer({ advanced: true, used: resilienceAttention }),
        description: "What needs attention across your resilience registers",
        keywords: ["resilience overview", "attention", "playbook", "ransomware"],
      },
      { id: "resilience-assets", label: "Assets", to: "/resilience/assets", deferred: defer({ advanced: true, used: used(usage?.assets) }), description: "Devices, systems and services you rely on", keywords: ["asset register", "assets", "inventory", "devices"] },
      {
        // Changes to systems and configuration, approved by someone else (docs/CHANGE-REGISTER.md).
        id: "resilience-changes", label: "Changes", to: "/changes",
        deferred: defer({ advanced: true, used: used(usage?.changes) }),
        description: "Changes to systems and configuration, approved, made and reviewed",
        keywords: ["change register", "change management", "change request", "emergency change", "cab", "a.8.32", "rollback"],
      },
      { id: "resilience-exercises", label: "Exercises", to: "/resilience/exercises", deferred: defer({ relevant: used(usage?.exercises), used: used(usage?.exercises) }), description: "Tabletops, restore tests and phishing simulations", keywords: ["exercises & testing", "tabletop", "backup restore", "phishing simulation"] },
      { id: "resilience-training", label: "Training", to: "/resilience/training", deferred: defer({ relevant: used(usage?.training) || used(usage?.trainingAssignments), used: used(usage?.training) || used(usage?.trainingAssignments) }), description: "Who has done security awareness training", keywords: ["training & awareness", "training", "awareness", "phishing"] },
      { id: "resilience-insurance", label: "Insurance", to: "/resilience/insurance", deferred: defer({ relevant: used(usage?.insurance), used: used(usage?.insurance) }), description: "Your cyber insurance cover and its conditions", keywords: ["cyber insurance", "insurance", "insurer", "cover"] },
      {
        // Shown to anyone the Cyber Security (Jersey) Law 2026 could designate: continuity is an Art. 29 security measure.
        id: "resilience-bia", label: "Business impact", to: "/resilience/bia", badge: "bia",
        deferred: defer({ relevant: counts?.oesPossible ?? false, used: used(usage?.businessImpact) || (counts?.bia ?? 0) > 0 }),
        description: "How long each essential service can be down, and whether it can recover in time",
        keywords: ["business impact analysis", "bia", "continuity", "rto", "essential service", "oes", "article 29"],
      },
      {
        id: "remediation", label: "Remediation", to: "/remediation", badge: "remediation",
        deferred: defer({ advanced: true, used: used(usage?.playbooks) || (counts?.remediation ?? 0) > 0 }),
        description: "Your rules for fixing things automatically, with approval for anything on a device",
        keywords: ["automation", "playbooks", "auto-remediation", "defender", "intune"],
      },
    ],
  };

  const settings: NavItem = {
    id: "settings-area", label: "Settings", icon: Building2,
    tabs: [
      { id: "org-profile", label: "Organisation", to: "/org-profile", hidden: !isAdmin, description: "Your company profile and sector", keywords: ["my organisation", "company", "profile", "sector"] },
      { id: "team", label: "Team", to: "/team", hidden: !isAdmin, description: "Members, roles and invitations", keywords: ["my team", "members", "invite", "roles", "sign-in", "sign in policy", "single sign-on", "sso", "mfa", "authenticator", "two-factor"] },
      { id: "my-package", label: "Package", to: "/my-package", hidden: !canBilling, description: "Your package, renewal and consultancy hours", keywords: ["my package", "package", "billing", "renewal", "consultancy hours"] },
      {
        id: "connections", label: "Connections", to: "/connections", hidden: !canIntegrate,
        lock: tierLock(tierLevel, CLIENT_MIN_TIER.connections, "Connections"),
        activeMatch: ["/m365", "/google", "/storage"],
        description: "Microsoft 365, Google and storage",
        keywords: ["integrations", "microsoft 365", "google", "intune"],
      },
      {
        id: "people", label: "Joiners & leavers", to: "/people", badge: "people", hidden: !canPeople,
        deferred: defer({ advanced: true, used: used(usage?.lifecycle) || (counts?.people ?? 0) > 0 }),
        description: "Onboarding and offboarding checklists that tick themselves",
        keywords: ["joiner mover leaver", "offboarding", "onboarding", "hr system"],
      },
      {
        id: "access-reviews", label: "Access reviews", to: "/access-reviews", hidden: !canPeople,
        deferred: defer({ advanced: true, used: used(usage?.accessReviews) }),
        description: "Managers confirm who still needs each group and application",
        keywords: ["access review", "user access review", "recertification"],
      },
      {
        id: "operating-parameters", label: "Operating parameters", to: "/operating-parameters", hidden: !isAdmin,
        description: "The deadlines and review cycles your registers run to, and the figures your policies set",
        keywords: ["operating parameters", "deadlines", "sla", "review cycle", "policy figures"],
      },
      { id: "activity-log", label: "Activity log", to: "/activity-log", deferred: defer({ advanced: true }), description: "Who changed what, and when", keywords: ["audit log", "history"] },
    ],
  };

  const help: NavItem = {
    id: "help-area", label: "Help", icon: CircleHelp,
    tabs: [
      { id: "help", label: "Help & guide", to: "/help", exact: true, description: "How the platform works", keywords: ["help", "guide", "faq"] },
      { id: "help-playbooks", label: "Playbooks", to: "/help/playbooks", description: "Good practice for incidents, suppliers, continuity and more", keywords: ["playbook", "incident response", "ransomware", "third party risk", "business continuity", "best practice", "nist", "ncsc"] },
      { id: "help-library", label: "Library", to: "/help/library", description: "The NIST, NCSC, ENISA and EDPB guidance the playbooks quote", keywords: ["library", "guidance", "nist", "ncsc", "enisa", "edpb", "training material", "reference"] },
      { id: "help-ask", label: "Ask your registers", to: "/help/ask", description: "Ask a question in words and get an answer from your own registers, each record linked", keywords: ["ask", "question", "search registers", "what is overdue", "query", "find records"] },
      { id: "consultancy-time", label: "Book a consultant", to: "/consultancy-time", hidden: !isAdmin, description: "Book your included consultancy hours", keywords: ["book consultancy time", "consultant", "booking", "hours", "12 hours"] },
    ],
  };

  // Playbooks: the organisation's own version of each good-practice playbook, as on the main
  // platform (docs/PLAYBOOKS.md). No data protection playbooks: the edition carries no DP module.
  const playbooksArea: NavItem = {
    id: "playbooks", label: "Playbooks", icon: BookOpenCheck, to: "/playbooks",
    description: "Your own playbooks, generated from your records beside the published guidance",
    keywords: ["playbook", "runbook", "procedure", "what to do", "incident response", "ransomware", "suppliers", "continuity"],
  };

  const sections: NavSection[] = [
    { id: "home", items: [home] },
    { id: "certification", label: "Certification & law", items: [certification, law] },
    { id: "work", label: "Work", items: [playbooksArea, documents, risks, incidents] },
    { id: "account", items: [settings, help] },
  ];
  return sections.filter((section) => section.items.some((item: NavItem) => !item.hidden));
}
